/* Deliberately the last item in the navigation. Reference material is not a front
   door, and treating it as one is how intranets die.

   Every link points at the training kit already published in
   00 RMS Company Hub / 04 Onboarding & Training. Drive enforces access, so a
   link only opens for someone the Hub is shared with. */

const pdf = (id: string) => `https://drive.google.com/file/d/${id}/view`
const slides = (id: string) => `https://docs.google.com/presentation/d/${id}`
const folder = (id: string) => `https://drive.google.com/drive/folders/${id}`

const GUIDES_FOLDER = folder('1eD4xsuLWgOeY4lJg-QQI-kxucgvJ3NFH')
const DECKS_FOLDER = folder('1K-O_pIQVnFwrSekhtfEY5TeRRUPwHs-u')

const START_HERE = [
  {
    name: 'Quick Reference', url: pdf('162oqc3p1XoDEyIkuWfSkSsB7zwAhJQWK'),
    what: 'One page. Pin it.',
    excerpt: 'The twelve drives, the six branches, where a document goes, and what to do when you cannot see a folder.',
  },
  {
    name: 'What RMS is and why', url: pdf('1wowsQp3g0ixDi_oGGkKZccCwLvw1__hE'),
    what: 'The structure explained.',
    excerpt: 'RMS is d.light’s first group-wide effort to give every record one home. Records are organised by operating company first: go to your OpCo and the six function branches are inside it, the same in every drive.',
  },
  {
    name: 'How to file a document', url: pdf('1RwsSziyJf5p_Rc64LcfNeDbtT6YI05Es'),
    what: 'The one rule, with worked examples.',
    excerpt: 'A record’s home is decided by whose obligation it serves, never by who wrote it. Binds one OpCo: that OpCo. Applies across OpCos: Group. Everyone should read it: Company Hub.',
    example: 'KE_LEG_DistributorAgreement_AcmeLtd_20260907',
  },
  {
    name: 'How to find records', url: pdf('1NmAoTHIoYRtY1dVErZgTOc-ShGgZLHjC'),
    what: 'Search, Shared with me, and requesting access.',
    excerpt: 'Folders granted to you appear under Shared with me, not Shared drives. A folder you were not granted does not appear at all. Search only ever returns what you can open.',
  },
  {
    name: 'Moving your files in', url: pdf('1Y1IqFcAI6dk5MMKJu5psQZ0tn4oeR5ZN'),
    what: 'What happens when, and what to do.',
    excerpt: 'There is no bulk migration. You move your own files from My Drive, Spaces and team drives, with My migration suggesting where each belongs. Nothing moves without your click. Check the destination first: once a file is in a shared drive you may not be able to move it out yourself.',
  },
  {
    name: 'Frequently asked questions', url: pdf('1SxNPwdbvXYIGJ5Znsdt1zWQJv77yk-uo'),
    what: 'The questions people actually ask.',
    excerpt: 'Why OpCo-first, whether you lose access, what ISO 15489 alignment means for v1.0, and what Phase 2 adds.',
  },
]

const ROLE_GUIDES = [
  { name: 'Every staff member', url: pdf('1Rd1TLmOck1-XLmud3Ei9LlztygJW8XRi') },
  { name: 'Country MD / General Manager', url: pdf('1RoJPgtGfmlKiKBMjSnZSyfiVglM1k6K3') },
  { name: 'Head of a function', url: pdf('1WztFJoH4Y1o-xSmnQkeE85SUcMqq9ffs') },
  { name: 'Group role', url: pdf('1LPGy4NouxCZ-W5wxelXDn1J4easOwCus') },
  { name: 'Records / folder owner', url: pdf('19SssQxP1AV3eO4nZJPPLfpL6otSFaZxt') },
]

/* The latest deck per department: the restructured _v02 decks (28-30 Sep), and the
   Finance deck delivered 28 Sep. v1 decks sit in "00 Archive (v1 decks)". */
const DEPARTMENT_DECKS = [
  { name: 'Procurement', url: slides('1f2lyOQVy0gqOhbQETk7Gsf2tWbHZU8WSFriZDn_IvtI') },
  { name: 'Sales', url: slides('1SvdLGY-LON5C_fobmWog2oMOYX_YTfZInplylmJl34U') },
  { name: 'Sales Operations', url: slides('1S01X0b3IvsL-dL-mW-pP2WPiNr_qLdI87679eRAQhug') },
  { name: 'Field Trainers', url: slides('1Tv6HCiQ2eVBwyjvyjDtnBCZpYxsEn6l0QfQausUBrmY') },
  { name: 'Finance', url: slides('165C4bBJqYYlJBYJZ_bFGfWYIrBj-jdUHgilKmnM6QV8') },
  { name: 'Legal', url: slides('1CyhJQtQ6w9F8OZYw6VED9fbRqZ1Y4iZSnoBPOhwxhZ8') },
  { name: 'HR (People)', url: slides('112__xqVeCNnT-Xah-Mk1ifU2rI0k2SajpojP66vcTas') },
  { name: 'Technology & Business Intelligence', url: slides('1ZqvDYxtL0lTVzXsy7qhDP0d7l0ed1kh4dQMf7mP2Y-M') },
  { name: 'Marketing', url: slides('1CTDha8Nw_PSuMNLFBM7vyaVmrnhn_iweBjZzi98FTSQ') },
  { name: 'Credit', url: slides('15l_XJQ7aBE4mWma1i7k79xprr9p5V6sRlp1QEEUTSZA') },
  { name: 'After Sales', url: slides('1kMKtbda22rBnn93zgnaW8XYX0sCDpljz0efFOFcD54g') },
  { name: 'Customer Experience', url: slides('1ZDuPwIAIDMUcRXdwZ2G8WxcUKffT3KlTXbCZUMcvr3o') },
  { name: 'Supply Chain', url: slides('1bSU5iEz8-ABS2zwPiwhrG9yFboXSjGqBkZWdxyBWNyE') },
  { name: 'Quality Assurance', url: slides('1dFXKC_a2TB6g2AvGT1A2Nk0uNsRatNhFwTOWNZu5Pmo') },
  { name: 'Strategy & Product', url: slides('1k8MVEKAS27RrUfitpJvefrpasZ2hANjj1Ech3U7tiZE') },
  { name: 'Admin', url: slides('1GGoV5VaVnJxaJqoDinTopKt39LaKa1HWU7EjFCKzvdU') },
  { name: 'ESG', url: slides('1s-6fANYwPV8-LScMDiS57YFDkf-zd4GRnw2Mt_omKkI') },
  { name: 'Global Partnerships', url: slides('1E1ViTd-B798FkSd6JSKmvv_u4fKLBA_8rRk1aGO94b8') },
]

function LinkList({ items }: { items: { name: string; url: string }[] }) {
  return (
    <ul className="rowlist" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {items.map((i) => (
        <li key={i.name} className="row">
          <span className="row__name">{i.name}</span>
          <a className="btn btn--ghost" href={i.url} target="_blank" rel="noreferrer">Open</a>
        </li>
      ))}
    </ul>
  )
}

export default function LearnRoute() {
  return (
    <>
      <p className="eyebrow">Learn</p>
      <h1 style={{ marginBottom: 'var(--s-sm)' }}>How RMS works</h1>
      <p className="note" style={{ marginBottom: 'var(--s-lg)' }}>
        You do not need to read these to use the portal: My access and Move a folder already apply
        the rules. Start with the Quick Reference, then your role guide and your
        department’s deck.
      </p>

      <h2 style={{ marginBottom: 'var(--s-md)' }}>Start here</h2>
      <div className="learn__grid">
        {START_HERE.map((d) => (
          <div className="block" key={d.name}>
            <div className="sectionhead" style={{ margin: 0 }}>
              <h3 style={{ margin: 0 }}>{d.name}</h3>
              <a className="btn btn--secondary" href={d.url} target="_blank" rel="noreferrer">Open</a>
            </div>
            <p className="note" style={{ marginTop: 'var(--s-xs)', marginBottom: 0 }}>{d.what}</p>
            <p style={{ marginTop: 'var(--s-sm)', marginBottom: 0 }}>{d.excerpt}</p>
            {d.example && (
              <p className="mono" style={{ marginTop: 'var(--s-sm)', marginBottom: 0, fontSize: '.82rem', color: 'var(--ink-2)' }}>
                Example: {d.example}
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="learn__panels" style={{ marginTop: 'var(--s-2xl)' }}>
        <div className="block">
          <div className="sectionhead" style={{ margin: 0 }}>
            <h3 style={{ margin: 0 }}>Your role guide</h3>
            <a className="btn btn--ghost" href={GUIDES_FOLDER} target="_blank" rel="noreferrer">All guides</a>
          </div>
          <p className="note" style={{ marginTop: 'var(--s-xs)' }}>What your role sees, owns and needs to check.</p>
          <LinkList items={ROLE_GUIDES} />
        </div>

        <div className="block">
          <div className="sectionhead" style={{ margin: 0 }}>
            <h3 style={{ margin: 0 }}>Your department’s training deck</h3>
            <a className="btn btn--ghost" href={DECKS_FOLDER} target="_blank" rel="noreferrer">All decks</a>
          </div>
          <p className="note" style={{ marginTop: 'var(--s-xs)' }}>Where your department’s records go, with examples from your own work.</p>
          <LinkList items={DEPARTMENT_DECKS} />
        </div>
      </div>

      <div className="block block--sunk" style={{ marginTop: "var(--s-md)" }}>
        <h3>Stuck?</h3>
        <p style={{ marginBottom: 0 }}>
          Ask in <a href="https://chat.google.com/room/AAQAKvxKrAw?cls=7" target="_blank" rel="noreferrer">#rms-help</a> on
          Google Chat, drop into an RMS clinic, or talk to your department’s RMS Champion.
        </p>
      </div>
    </>
  )
}
